<template>
    <div
        :class="twMerge('inline-flex items-center justify-items-start gap-1 paragraph-sm w-full h-8 ',
                        rootClass,
                        attrs.class as string,
        )"
        @click="inputRef?.focus()"
    >
        <!-- prefix -->
        <div v-if="hasPrefix" class="flex items-center gap-2">
            <Icon v-if="props.leadingIcon" :name="props.leadingIcon" :size="16" class="text-secodary" />
            <span v-if="props.prefix" class="text-disabled paragraph-sm select-none">{{ props.prefix }}</span>
        </div>

        <input
            v-bind="forwardedAttrs"
            ref="inputRef"
            v-model="modelValue"
            :placeholder="placeholder"
            :class="twMerge(
                'h-full w-full p-0 bg-white/0',
                'border-none focus:outline-none focus:ring-0  paragraph-sm',
            )"
            :disabled="props.disabled"
            :readonly="props.readonly"
        >

        <!-- suffix -->
        <div v-if="hasSuffix" class="flex items-center gap-2">
            <span v-if="props.suffix" class="text-disabled paragraph-sm select-none">{{ props.suffix }}</span>
            <FormFieldValidationIcon :state="validationMarker" />
            <Icon v-if="props.trailingIcon" :name="props.trailingIcon" :size="16" class="text-secondary" />
        </div>
    </div>
</template>

<script setup lang="ts">

import { computed, onMounted, ref, useAttrs } from 'vue'
import { useVModel } from '@vueuse/core'
import { twMerge } from 'tailwind-merge'
import FormFieldValidationIcon from '../forms/FormFieldValidationIcon.vue'
import { useFieldStateStyles } from '../forms/useFieldStateStyles'
import Icon from '../icon/Icon.vue'
import type { FormInputProps } from './types'

defineOptions({
    inheritAttrs: false,
})

const props = defineProps<FormInputProps>()

const attrs = useAttrs()
const forwardedAttrs = computed(() => {
    const { class: _class, required: _required, ...rest } = attrs
    return rest
})

const { rootClass, validationMarker } = useFieldStateStyles(props)

const hasPrefix = computed(() => props.prefix || props.leadingIcon)
const hasSuffix = computed(() => props.suffix || props.trailingIcon || validationMarker.value)

const emit = defineEmits<{
    (e: 'update:modelValue', payload: string | number): void
}>()

const modelValue = useVModel(props, 'modelValue', emit, {
    passive: true,
    defaultValue: props.defaultValue,
})

const inputRef = ref<HTMLInputElement>()

// Handle autofocus prop
onMounted(() => {
    if (props.autofocus) {
        inputRef.value?.focus()
    }
})

defineExpose({
    focus: () => inputRef.value?.focus(),
    inputRef,
})

</script>
